import {
  formatTimeSl,
  minutesToTime,
  nowTimeIn,
  timeToMinutes,
  type CheckinStatus,
  type Occurrence,
  type SleepTimes,
} from '@planer/shared';
import { CalendarDays, Check, Minus, X } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { occColor } from '../lib/colors';
import { firstVisibleHour, layoutDay } from '../lib/dayLayout';
import { timeLabel } from '../lib/format';
import { SleepShade } from './SleepShade';
import { TravelStrip } from './TravelStrip';

const HOUR_PX = 64;
const GUTTER = '3.25rem';

interface DayGridProps {
  occs: readonly Occurrence[];
  /** Shades the night (null on a holiday). */
  sleep: SleepTimes | null;
  isToday: boolean;
  now: Date;
  zone: string;
  status: (key: string) => CheckinStatus | null | undefined;
  onOpen: (occ: Occurrence) => void;
  onToggle: (occ: Occurrence) => void;
  /** Tap on an empty slot: add something at this time. */
  onAddAt: (time: string) => void;
}

/** The day as a calendar: blocks as tall as they last, overlaps side by side, a "now" line. */
export function DayGrid({
  occs,
  sleep,
  isToday,
  now,
  zone,
  status,
  onOpen,
  onToggle,
  onAddAt,
}: DayGridProps) {
  const blocks = useMemo(() => layoutDay(occs.filter((o) => !o.untimed)), [occs]);
  const startHour = Math.min(
    firstVisibleHour(blocks),
    sleep ? Math.floor(timeToMinutes(sleep.wake) / 60) : 24,
  );
  const hours = Array.from({ length: 24 - startHour }, (_, i) => startHour + i);
  const top = (min: number) => ((min - startHour * 60) / 60) * HOUR_PX;
  const nowTime = nowTimeIn(zone, now);
  const nowMin = timeToMinutes(nowTime);
  const nowRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLDivElement>(null);

  // Open where it matters: "now" today, otherwise the first entry.
  useEffect(() => {
    (isToday ? nowRef.current : firstRef.current)?.scrollIntoView({ block: 'center' });
  }, [isToday]);

  function onBackgroundClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const minutes = startHour * 60 + ((e.clientY - rect.top) / HOUR_PX) * 60;
    onAddAt(minutesToTime(Math.floor(minutes / 15) * 15));
  }

  return (
    <section aria-label="Dan kot koledar" className="card relative overflow-hidden px-2 py-3">
      <div
        className="relative cursor-copy"
        style={{ height: hours.length * HOUR_PX }}
        onClick={onBackgroundClick}
        data-testid="day-grid"
      >
        {sleep && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0"
            style={{ left: GUTTER }}
          >
            <SleepShade sleep={sleep} top={top} height={hours.length * HOUR_PX} />
          </div>
        )}
        {hours.map((h) => (
          <div
            key={h}
            aria-hidden
            className="pointer-events-none absolute right-0 left-0 border-t border-line"
            style={{ top: top(h * 60) }}
          >
            <span className="absolute -top-2.5 left-0 w-11 pr-1 text-right text-[12px] font-medium text-muted tabular-nums">
              {h}:00
            </span>
          </div>
        ))}

        {blocks.map((b, i) => {
          const o = b.occ;
          const s = status(o.key);
          const color = occColor(o);
          const height = Math.max(22, (b.spanMin / 60) * HOUR_PX - 3);
          const compact = height < 44;
          const isEvent = o.source === 'event';
          const muted = s === 'done' || s === 'skipped';
          return (
            <div
              key={o.key}
              ref={i === 0 ? firstRef : undefined}
              className="absolute"
              style={{
                top: top(b.startMin) + 1,
                height,
                left: `calc(${GUTTER} + (100% - ${GUTTER}) * ${b.column} / ${b.columns})`,
                width: `calc((100% - ${GUTTER}) / ${b.columns} - 3px)`,
              }}
            >
              <TravelStrip
                side="before"
                minutes={b.travelBefore}
                color={color}
                top={-(b.travelBefore / 60) * HOUR_PX}
                height={(b.travelBefore / 60) * HOUR_PX - 1}
                onOpen={() => {
                  onOpen(o);
                }}
              />
              <TravelStrip
                side="after"
                minutes={b.travelAfter}
                color={color}
                top={height + 2}
                height={(b.travelAfter / 60) * HOUR_PX - 2}
                onOpen={() => {
                  onOpen(o);
                }}
              />
              <button
                type="button"
                data-testid="grid-block"
                data-status={s ?? 'open'}
                onClick={() => {
                  onOpen(o);
                }}
                aria-label={`${timeLabel(o)}, ${o.title}${isEvent ? `, koledar ${o.calendar?.name ?? ''}` : ''}${
                  s
                    ? `, ${s === 'done' ? 'opravljeno' : s === 'missed' ? 'ni opravljeno' : 'preskočeno'}`
                    : ''
                }`}
                className={`tap relative flex h-full w-full overflow-hidden rounded-lg text-left ${
                  compact ? 'items-center px-2' : 'flex-col px-2 py-1.5'
                }`}
                style={{
                  // Done/skipped blocks fade to grey instead of transparency, so text keeps its contrast.
                  background: `color-mix(in srgb, ${color} ${muted ? 6 : isEvent ? 10 : 16}%, var(--surface))`,
                  boxShadow: `inset 3px 0 0 ${color}${isEvent ? `, inset 0 0 0 1px color-mix(in srgb, ${color} 35%, transparent)` : ''}`,
                }}
              >
                <span
                  className={`min-w-0 truncate pl-1 text-[13px] leading-tight font-semibold ${
                    muted ? 'text-text-2 line-through' : ''
                  } ${o.requiresCheck && !compact ? 'pr-6' : ''}`}
                >
                  {compact && (
                    <span className="mr-1.5 font-medium text-text-2 tabular-nums">
                      {formatTimeSl(o.startTime)}
                    </span>
                  )}
                  {o.title}
                </span>
                {!compact && (
                  <span className="flex min-w-0 items-center gap-1 truncate pl-1 text-[12px] text-text-2 tabular-nums">
                    {isEvent && <CalendarDays size={11} className="shrink-0" aria-hidden />}
                    {timeLabel(o)}
                    {o.location ? ` · ${o.location}` : ''}
                  </span>
                )}
              </button>
              {o.requiresCheck && !compact && (
                <button
                  type="button"
                  onClick={() => {
                    onToggle(o);
                  }}
                  aria-label={
                    s === 'done'
                      ? `${o.title}: prekliči opravljeno`
                      : `Označi ${o.title} kot opravljeno`
                  }
                  className="absolute top-0 right-0 grid h-9 w-9 place-items-center"
                >
                  <span
                    className="grid h-5 w-5 place-items-center rounded-full"
                    style={{
                      background:
                        s === 'done'
                          ? 'var(--done)'
                          : s === 'missed'
                            ? 'var(--missed)'
                            : s === 'skipped'
                              ? 'var(--skip)'
                              : 'var(--surface)',
                      boxShadow: s ? 'none' : `inset 0 0 0 2px ${color}`,
                    }}
                  >
                    {s === 'done' && <Check size={12} strokeWidth={3.5} color="var(--on-status)" />}
                    {s === 'missed' && <X size={12} strokeWidth={3.5} color="var(--on-status)" />}
                    {s === 'skipped' && (
                      <Minus size={12} strokeWidth={3.5} color="var(--on-status)" />
                    )}
                  </span>
                </button>
              )}
            </div>
          );
        })}

        {isToday && nowMin >= startHour * 60 && (
          <div
            ref={nowRef}
            aria-label={`Zdaj, ${formatTimeSl(nowTime)}`}
            className="pointer-events-none absolute right-0 left-0 z-10 flex items-center"
            style={{ top: top(nowMin) - 5 }}
          >
            <span className="w-11 pr-1 text-right text-[11px] font-bold text-accent tabular-nums">
              {formatTimeSl(nowTime)}
            </span>
            <span className="ml-1 h-2.5 w-2.5 rounded-full bg-accent" />
            <span className="h-[2px] flex-1 bg-accent" />
          </div>
        )}
      </div>
    </section>
  );
}
